import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
// api.forgotPassword / api.resetPassword added in lib/api.ts

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<'request' | 'reset'>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const [loading, setLoading] = useState(false)

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setLoading(true)
    try {
      await api.forgotPassword(email)
      setInfo('If an account exists for that email, a 6-digit reset code is on its way. Check spam too.')
      setStep('reset')
    } catch {
      setError('Something went wrong. Please try again.')
    } finally { setLoading(false) }
  }

  const doReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(''); setLoading(true)
    try {
      await api.resetPassword(email, code, newPassword)
      navigate('/login', { state: { message: 'Password updated. Sign in with your new password.' } })
    } catch (err: any) {
      setError(err?.message || 'Reset failed. Check the code and try again.')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-brand-600 dark:text-brand-400">Dr. Scribey</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Reset your password</p>
        </div>
        <div className="bg-white dark:bg-gray-950 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800 p-6 space-y-4">
          {info && <div className="text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg px-3 py-2">{info}</div>}
          {error && <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2">{error}</div>}

          {step === 'request' ? (
            <form onSubmit={requestCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="you@example.com" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-800 text-white font-medium py-2.5 rounded-lg transition-colors">
                {loading ? 'Sending…' : 'Email me a reset code'}
              </button>
            </form>
          ) : (
            <form onSubmit={doReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reset code</label>
                <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} required
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-900 dark:text-gray-100 tracking-widest focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="123456" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                  className="w-full bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="At least 10 characters, mixed case + number" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-800 text-white font-medium py-2.5 rounded-lg transition-colors">
                {loading ? 'Resetting…' : 'Set new password'}
              </button>
              <button type="button" onClick={() => { setStep('request'); setInfo(''); }}
                className="w-full text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                Didn't get a code? Send again
              </button>
            </form>
          )}

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            <Link to="/login" className="text-brand-400 hover:text-brand-300">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
